/**
 * Dynamix compression type 3 — LH1 / LZHUF: LZSS over a 4096-byte ring
 * buffer, literal/length alphabet coded by an adaptive Huffman tree,
 * match-position upper bits by a static table. This is Haruyasu
 * Yoshizaki's `lzhuf.c` (1988), unmodified in algorithm;
 * docs/formats/compression.md § Type 0x03 has the parameter table this
 * implementation follows. Bit packing is MSB-first within each byte
 * (unlike LZW's LSB-first) — bytes still consumed in order.
 *
 * VERIFIED: all 43 members of the expansion's RESOURCE.000 decompress to
 * exactly their declared `uncompressedSize` AND byte-exact bytes, checked
 * against Dynamix's own `UNCHUNK.EXE` unpacker run under DOSBox-X (the
 * independent oracle; see re-sessions/lh1/ and
 * docs/formats/resource-volumes.md § LH1 anomaly, 2026-08-19). 39 of the 43
 * are additionally byte-identical to their loose-file counterparts; the
 * other 4 (`PS.EXE`, `RB.EXE`, `RBMB.EXE`, `RBMB.OVL`) have a different
 * *length* than their loose counterparts, which alone proves they're
 * genuinely different revisions (confirmed by the oracle too, not just
 * "right length" — see the LH1 anomaly note before trusting any future
 * "close but not identical" result as corruption without an independent
 * decoder to back it up).
 */

const N = 4096; // ring buffer size
const F = 60; // max match length (lookahead size)
const THRESHOLD = 2;
const N_CHAR = 256 - THRESHOLD + F; // 314
const T = N_CHAR * 2 - 1; // 627, size of the Huffman node table
const R = T - 1; // 626, root position
const MAX_FREQ = 0x8000;

// Static tables for decoding the upper 6 bits of a 12-bit match position
// from the first byte read. Standard lzhuf.c tables (public-domain-ish
// 1988 algorithm; this transcription cross-referenced against Nyerguds'
// WTFPL `DynamixLzHuffDecoder.cs`, which independently reimplements the
// same well-known lzhuf.c constant tables).
// prettier-ignore
const D_CODE: readonly number[] = [
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x01, 0x01, 0x01, 0x01, 0x01, 0x01, 0x01, 0x01, 0x01, 0x01, 0x01, 0x01, 0x01, 0x01, 0x01, 0x01,
  0x02, 0x02, 0x02, 0x02, 0x02, 0x02, 0x02, 0x02, 0x02, 0x02, 0x02, 0x02, 0x02, 0x02, 0x02, 0x02,
  0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03,
  0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05,
  0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07,
  0x08, 0x08, 0x08, 0x08, 0x08, 0x08, 0x08, 0x08, 0x09, 0x09, 0x09, 0x09, 0x09, 0x09, 0x09, 0x09,
  0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0a, 0x0b, 0x0b, 0x0b, 0x0b, 0x0b, 0x0b, 0x0b, 0x0b,
  0x0c, 0x0c, 0x0c, 0x0c, 0x0d, 0x0d, 0x0d, 0x0d, 0x0e, 0x0e, 0x0e, 0x0e, 0x0f, 0x0f, 0x0f, 0x0f,
  0x10, 0x10, 0x10, 0x10, 0x11, 0x11, 0x11, 0x11, 0x12, 0x12, 0x12, 0x12, 0x13, 0x13, 0x13, 0x13,
  0x14, 0x14, 0x14, 0x14, 0x15, 0x15, 0x15, 0x15, 0x16, 0x16, 0x16, 0x16, 0x17, 0x17, 0x17, 0x17,
  0x18, 0x18, 0x19, 0x19, 0x1a, 0x1a, 0x1b, 0x1b, 0x1c, 0x1c, 0x1d, 0x1d, 0x1e, 0x1e, 0x1f, 0x1f,
  0x20, 0x20, 0x21, 0x21, 0x22, 0x22, 0x23, 0x23, 0x24, 0x24, 0x25, 0x25, 0x26, 0x26, 0x27, 0x27,
  0x28, 0x28, 0x29, 0x29, 0x2a, 0x2a, 0x2b, 0x2b, 0x2c, 0x2c, 0x2d, 0x2d, 0x2e, 0x2e, 0x2f, 0x2f,
  0x30, 0x31, 0x32, 0x33, 0x34, 0x35, 0x36, 0x37, 0x38, 0x39, 0x3a, 0x3b, 0x3c, 0x3d, 0x3e, 0x3f,
];

// Fixed 2026-08-19 (LH1 anomaly investigation): two rows of this table were
// transcribed one value too high — row 8 (indices 128-143) was 0x06 instead
// of 0x05, and row 11 (indices 176-191) was 0x07 instead of 0x06. Confirmed
// against Nyerguds' `DynamixLzHuffDecoder.cs` (`_dLen`), which has the
// correct {3:32, 4:48, 5:64, 6:48, 7:48, 8:16} value distribution — our
// table previously had {3:32, 4:48, 5:48, 6:48, 7:64, 8:16}, silently
// skewed by 16 entries in each direction. A `D_LEN[i]` that's one too high
// makes `decodePosition()` consume one extra bit for every match whose
// `getByte()` result lands in the wrong row, desyncing the bit stream for
// the rest of the file from that point on — the "right length, garbage
// bytes" signature documented in docs/formats/resource-volumes.md § LH1
// anomaly. See lh1.test.ts for the regression coverage.
// prettier-ignore
const D_LEN: readonly number[] = [
  0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03,
  0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03, 0x03,
  0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04,
  0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04,
  0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04, 0x04,
  0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05,
  0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05,
  0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05,
  0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05, 0x05,
  0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06,
  0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06,
  0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06, 0x06,
  0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07,
  0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07,
  0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07, 0x07,
  0x08, 0x08, 0x08, 0x08, 0x08, 0x08, 0x08, 0x08, 0x08, 0x08, 0x08, 0x08, 0x08, 0x08, 0x08, 0x08,
];

/**
 * One decompression run's state. Bit I/O per lzhuf.c `GetBit`/`GetByte`:
 * a read past end-of-input yields 0 bits rather than erroring — the
 * decoder only ever reads as many bits as the Huffman tree and match
 * lengths call for, up to `uncompressedSize` output bytes, so trailing
 * zero-padding past the real stream is harmless and expected at the tail
 * of the last codeword.
 */
class Lh1Decoder {
  private readonly payload: Uint8Array;
  private inPos = 0;
  private bitBuffer = 0; // holds up to 16 bits, left-justified in a 16-bit field
  private bitCount = 0;

  private readonly freq = new Uint32Array(T + 1);
  private readonly parent = new Int32Array(T + N_CHAR);
  private readonly son = new Int32Array(T);

  constructor(payload: Uint8Array) {
    this.payload = payload;
    this.startHuff();
  }

  decode(uncompressedSize: number): Uint8Array {
    const textBuf = new Uint8Array(N + F - 1);
    textBuf.fill(0x20, 0, N - F);
    let r = N - F;

    const out = new Uint8Array(uncompressedSize);
    let outPos = 0;

    while (outPos < uncompressedSize) {
      const c = this.decodeChar();
      if (c < 256) {
        out[outPos++] = c;
        textBuf[r++] = c;
        r &= N - 1;
      } else {
        const matchLength = c - 255 + THRESHOLD;
        const matchStart = (r - this.decodePosition() - 1) & (N - 1);
        for (let k = 0; k < matchLength && outPos < uncompressedSize; k++) {
          const byte = textBuf[(matchStart + k) & (N - 1)]!;
          out[outPos++] = byte;
          textBuf[r++] = byte;
          r &= N - 1;
        }
      }
    }

    return out;
  }

  private nextInputByte(): number {
    return this.inPos < this.payload.length ? this.payload[this.inPos++]! : 0;
  }

  private fillBits(): void {
    while (this.bitCount <= 8) {
      this.bitBuffer |= this.nextInputByte() << (8 - this.bitCount);
      this.bitCount += 8;
    }
  }

  private getBit(): number {
    this.fillBits();
    const bit = (this.bitBuffer & 0x8000) !== 0 ? 1 : 0;
    this.bitBuffer = (this.bitBuffer << 1) & 0xffff;
    this.bitCount--;
    return bit;
  }

  private getByte(): number {
    this.fillBits();
    const value = (this.bitBuffer >> 8) & 0xff;
    this.bitBuffer = (this.bitBuffer << 8) & 0xffff;
    this.bitCount -= 8;
    return value;
  }

  private startHuff(): void {
    const { freq, parent, son } = this;
    for (let i = 0; i < N_CHAR; i++) {
      freq[i] = 1;
      son[i] = i + T;
      parent[i + T] = i;
    }
    let i = 0;
    let j = N_CHAR;
    while (j <= R) {
      freq[j] = freq[i]! + freq[i + 1]!;
      son[j] = i;
      parent[i] = j;
      parent[i + 1] = j;
      i += 2;
      j++;
    }
    freq[T] = 0xffff;
    parent[R] = 0;
  }

  /** Halves all frequencies and rebuilds the tree — lzhuf.c `reconst()`. */
  private reconst(): void {
    const { freq, parent, son } = this;
    // Collect leaves into the front of the table, halving their frequency.
    let j = 0;
    for (let i = 0; i < T; i++) {
      if (son[i]! >= T) {
        freq[j] = (freq[i]! + 1) >> 1;
        son[j] = son[i]!;
        j++;
      }
    }
    // Rebuild internal nodes by pairing adjacent (sorted-by-freq) entries.
    for (let i = 0, jj = N_CHAR; jj < T; i += 2, jj++) {
      const f = freq[i]! + freq[i + 1]!;
      freq[jj] = f;
      let k = jj - 1;
      while (f < freq[k]!) k--;
      k++;
      // Shift the `jj - k` already-sorted entries in [k, jj) up by one slot
      // to open a gap at k for the new combined node, keeping freq[0..jj)
      // sorted ascending (classic sorted-array Huffman-forest rebuild).
      const span = jj - k;
      freq.copyWithin(k + 1, k, k + span);
      freq[k] = f;
      son.copyWithin(k + 1, k, k + span);
      son[k] = i;
    }
    // Reconnect parent pointers.
    for (let i = 0; i < T; i++) {
      const k = son[i]!;
      parent[k] = i;
      if (k < T) parent[k + 1] = i;
    }
  }

  /** Bumps `code`'s frequency and re-sorts the tree along its path — lzhuf.c `update()`. */
  private update(code: number): void {
    const { freq, parent, son } = this;
    if (freq[R] === MAX_FREQ) {
      this.reconst();
    }
    let c = parent[code + T]!;
    do {
      const k = ++freq[c]!;
      let l = c + 1;
      if (k > freq[l]!) {
        while (k > freq[++l]!) {
          /* advance */
        }
        l--;
        freq[c] = freq[l]!;
        freq[l] = k;

        const i = son[c]!;
        parent[i] = l;
        if (i < T) parent[i + 1] = l;

        const j = son[l]!;
        son[l] = i;
        parent[j] = c;
        if (j < T) parent[j + 1] = c;
        son[c] = j;

        c = l;
      }
      c = parent[c]!;
    } while (c !== 0);
  }

  private decodeChar(): number {
    let c = this.son[R]!;
    while (c < T) {
      c = this.son[c + this.getBit()]!;
    }
    c -= T;
    this.update(c);
    return c;
  }

  private decodePosition(): number {
    const i = this.getByte();
    const high = D_CODE[i]! << 6;
    const len = D_LEN[i]! - 2;
    let low = i;
    for (let k = 0; k < len; k++) {
      low = (low << 1) + this.getBit();
    }
    return high | (low & 0x3f);
  }
}

export function decodeLh1(payload: Uint8Array, uncompressedSize: number): Uint8Array {
  if (uncompressedSize === 0) {
    return new Uint8Array(0);
  }
  return new Lh1Decoder(payload).decode(uncompressedSize);
}
